/**
 * ICE Server Prefetch
 *
 * Fires a fetch for TURN credentials at module evaluation time -- before React
 * mounts. When credentials arrive, immediately pre-warms an RTCPeerConnection
 * with iceCandidatePoolSize so STUN/TURN binding starts as early as possible.
 */
import constants from '@playcastdotio/GlobalConfig';
import { sendAnalytics } from '@playcastdotio/Analytics';

export type IceServerCredentials = {
  urls: string[];
  username: string;
  credential: string;
};

let cachedCredentials: IceServerCredentials | null = null;

function getQuickstartSlugFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const match = window.location.pathname.match(/^\/q\/([^/]+)/);
  return match?.[1] || undefined;
}

function getBeaconShareCodeFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const match = window.location.pathname.match(/^\/j\/([^/]+)/);
  return match?.[1] || undefined;
}

const quickstartSlug = getQuickstartSlugFromUrl();
const isQuickstartPage = quickstartSlug !== undefined;

const beaconShareCode = getBeaconShareCodeFromUrl();
const isBeaconPage = beaconShareCode !== undefined;

if (isQuickstartPage) {
  sendAnalytics({
    name: 'ICE Server Prefetch Started',
    role: 'player',
    flow: 'quickstart',
    quickstartSlug,
  });
}

if (isBeaconPage) {
  sendAnalytics({
    name: 'ICE Server Prefetch Started',
    role: 'player',
    flow: 'beacon',
    shareCode: beaconShareCode,
  });
}

const iceServerPromise: Promise<IceServerCredentials | null> = fetch(
  `${constants.REALTIME_API_BASE_URL}realtime/ice-servers`,
)
  .then((r) => (r.ok ? r.json() : null))
  .then((data) => data?.iceServers ?? null)
  .then((creds) => {
    cachedCredentials = creds;
    return creds;
  })
  .catch(() => null);

if (isQuickstartPage) {
  iceServerPromise.then((creds) => {
    sendAnalytics({
      name: 'ICE Server Prefetch Completed',
      role: 'player',
      flow: 'quickstart',
      quickstartSlug,
      success: creds !== null,
    });
  });
}

if (isBeaconPage) {
  iceServerPromise.then((creds) => {
    sendAnalytics({
      name: 'ICE Server Prefetch Completed',
      role: 'player',
      flow: 'beacon',
      shareCode: beaconShareCode,
      success: creds !== null,
    });
  });
}

// Pre-warm the RTCPeerConnection the instant credentials arrive.
// Import is lazy to avoid circular dependency (peer.ts imports us).
iceServerPromise.then((creds) => {
  if (!creds) return;
  import('./peer').then(({ preWarmPeerConnection }) =>
    preWarmPeerConnection(creds).catch((err) =>
      console.warn('[iceServerPrefetch] Pre-warm failed:', err),
    ),
  );
});

export function getPreFetchedIceServers(): Promise<IceServerCredentials | null> {
  return iceServerPromise;
}

/**
 * Synchronously check if ICE servers have already been fetched.
 * Returns true if the prefetch resolved successfully.
 */
export function hasPreFetchedIceServers(): boolean {
  return cachedCredentials !== null;
}
